import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, X, Send, ChevronDown, User, Plus, Search, HelpCircle, Users, UserPlus, Heart, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const isAuthenticated = !!token && !!user;
  // Hide the marketplace chat widget when the user is inside the Doserne
  // Academy portal — that portal ships its own Messenger-style ChatFab
  // (see /app/frontend/src/pages/doserne/ChatFab.jsx). Rendering both
  // stacked them in the same corner on mobile.
  const onDoserne = location.pathname.startsWith("/doserne");
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [friends, setFriends] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [codeSearchTerm, setCodeSearchTerm] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchingCode, setSearchingCode] = useState(false);
  const [projectChats, setProjectChats] = useState([]);
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatRef.current && !chatRef.current.contains(event.target) && isOpen && !isMinimized) {
        const chatButton = document.querySelector('[data-chat-button]');
        if (chatButton && chatButton.contains(event.target)) return;
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMinimized]);

  // Fetch data on open
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchConversations();
      fetchTeamMembers();
      fetchFriends();
      fetchProjectChats();
    }
  }, [isOpen, isAuthenticated]);

  // Fetch unread count on mount and when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadCount();
    }
  }, [isAuthenticated]);

  // Fetch messages when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.conversation_id);
    }
  }, [activeConversation]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Poll for new messages
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (activeConversation) {
        fetchMessages(activeConversation.conversation_id);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isAuthenticated, activeConversation]);

  const fetchUnreadCount = async () => {
    // Skip if no auth token — prevents noisy 403s before login completes
    const token = localStorage.getItem("scaffoldx_token") || localStorage.getItem("DOSERNE_TOKEN");
    if (!token) return;
    try {
      const res = await axios.get(`${API}/chat/unread-count`);
      setUnreadCount(res.data.unread_count || res.data.count || 0);
    } catch (err) {
      if (err?.response?.status !== 403) {
        console.error("Failed to fetch unread count:", err);
      }
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations`);
      setConversations(res.data || []);
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await axios.get(`${API}/chat/team-members`);
      const members = res.data || [];
      setAdmins(members.filter(m => m.role === 'platform_admin' || m.role === 'operations_manager'));
      setTeamMembers(members.filter(m => m.role !== 'platform_admin' && m.role !== 'operations_manager'));
    } catch (err) {
      console.error("Failed to fetch team members:", err);
    }
  };

  const fetchFriends = async () => {
    try {
      const res = await axios.get(`${API}/chat/friends`);
      setFriends(res.data || []);
    } catch (err) {
      console.error("Failed to fetch friends:", err);
    }
  };

  const fetchProjectChats = async () => {
    try {
      const res = await axios.get(`${API}/chat/project-chats`);
      setProjectChats(res.data || []);
    } catch (err) {
      console.error("Failed to fetch project chats:", err);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await axios.get(`${API}/chat/messages/${conversationId}`);
      setMessages(res.data || []);
      await axios.put(`${API}/chat/conversations/${conversationId}/read`);
      fetchUnreadCount();
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;

    setLoading(true);
    try {
      await axios.post(`${API}/chat/messages`, {
        conversation_id: activeConversation.conversation_id,
        content: newMessage.trim()
      });
      setNewMessage("");
      fetchMessages(activeConversation.conversation_id);
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async (recipientId, recipientName) => {
    try {
      const res = await axios.post(`${API}/chat/conversations`, {
        recipient_id: recipientId
      });
      setActiveConversation({
        conversation_id: res.data.conversation_id,
        participant_name: recipientName,
        participant_id: recipientId
      });
      setShowNewChat(false);
      setShowHelp(false);
      setShowAddFriend(false);
      fetchConversations();
    } catch (err) {
      console.error("Failed to start conversation:", err);
    }
  };

  const joinProjectChat = async (projectId, projectTitle) => {
    try {
      const res = await axios.post(`${API}/chat/project-chat/join`, {
        project_id: projectId
      });
      setActiveConversation({
        conversation_id: res.data.conversation_id,
        participant_name: `📋 ${projectTitle}`,
        is_project_chat: true,
        project_id: projectId
      });
      setShowNewChat(false);
      fetchConversations();
    } catch (err) {
      console.error("Failed to join project chat:", err);
    }
  };

  // Search user by code
  const searchByCode = async () => {
    if (!codeSearchTerm.trim()) return;
    
    setSearchingCode(true);
    setSearchResult(null);
    
    try {
      const res = await axios.get(`${API}/chat/search-by-code/${codeSearchTerm.trim()}`);
      setSearchResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "User not found");
    } finally {
      setSearchingCode(false);
    }
  };

  // Add friend
  const addFriend = async (userId, code) => {
    try {
      const res = await axios.post(`${API}/chat/friends/add`, {
        user_id: userId,
        code: code
      });
      toast.success(res.data.message);
      fetchFriends();
      fetchTeamMembers();
      setSearchResult(null);
      setCodeSearchTerm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add friend");
    }
  };

  // Filter members by search
  const filteredMembers = teamMembers.filter(m => 
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.worker_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAuthenticated) return null;
  if (onDoserne) return null;

  // Minimized state
  if (isMinimized) {
    return (
      <button
        data-chat-button
        onClick={() => {
          setIsMinimized(false);
          setIsOpen(true);
        }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#23F7DD] text-[#050505] rounded-full shadow-lg hover:bg-[#70FFEF] transition-all flex items-center justify-center"
      >
        <MessageSquare className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <>
      {/* Chat Button */}
      <button
        data-chat-button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#23F7DD] text-[#050505] rounded-full shadow-lg hover:bg-[#70FFEF] transition-all flex items-center justify-center"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageSquare className="w-6 h-6" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={chatRef}
          className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 h-[520px] bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="p-3 bg-[#111] border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#23F7DD]" />
              <span className="font-semibold text-white text-sm truncate max-w-[150px]">
                {activeConversation ? activeConversation.participant_name : 
                 showNewChat ? t('chat.newMessage') : 
                 showHelp ? t('common.info') : 
                 showAddFriend ? t('common.add') : t('chat.conversations')}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {!activeConversation && !showNewChat && !showHelp && !showAddFriend && (
                <>
                  <button
                    onClick={() => setShowAddFriend(true)}
                    className="p-1.5 text-pink-400 hover:bg-pink-500/10 rounded"
                    title="Add Friend"
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowHelp(true)}
                    className="p-1.5 text-yellow-400 hover:bg-yellow-500/10 rounded"
                    title="Get Help"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowNewChat(true)}
                    className="p-1.5 text-[#23F7DD] hover:bg-[#23F7DD]/10 rounded"
                    title="New Chat"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </>
              )}
              {(activeConversation || showNewChat || showHelp || showAddFriend) && (
                <button
                  onClick={() => {
                    setActiveConversation(null);
                    setShowNewChat(false);
                    setShowHelp(false);
                    setShowAddFriend(false);
                    setSearchResult(null);
                    setCodeSearchTerm("");
                  }}
                  className="p-1.5 text-[#94A3B8] hover:text-white hover:bg-white/10 rounded"
                  title="Back"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => {
                  setIsMinimized(true);
                  setIsOpen(false);
                }}
                className="p-1.5 text-[#94A3B8] hover:text-white hover:bg-white/10 rounded"
                title="Minimize"
              >
                <span className="text-lg leading-none">−</span>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-[#94A3B8] hover:text-red-400 hover:bg-red-500/10 rounded"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {showAddFriend ? (
              // Add Friend by Code
              <div className="h-full flex flex-col">
                <div className="p-3 bg-pink-500/10 border-b border-pink-500/30">
                  <p className="text-sm text-pink-400">
                    Add a friend by their code (WRK-xxx or TL-xxx)
                  </p>
                </div>
                <div className="p-3 border-b border-white/10">
                  <div className="flex gap-2">
                    <Input
                      value={codeSearchTerm}
                      onChange={(e) => setCodeSearchTerm(e.target.value.toUpperCase())}
                      onKeyPress={(e) => e.key === "Enter" && searchByCode()}
                      placeholder="Enter code (e.g., WRK-021)"
                      className="bg-white/5 border-white/10 text-white text-sm"
                    />
                    <Button
                      onClick={searchByCode}
                      disabled={searchingCode || !codeSearchTerm.trim()}
                      className="bg-pink-500 text-white hover:bg-pink-600 px-3"
                    >
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Search Result */}
                {searchResult && (
                  <div className="p-3 border-b border-white/10">
                    <div className="p-3 bg-white/5 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-pink-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{searchResult.name}</p>
                          <p className="text-xs text-[#94A3B8]">{searchResult.worker_code}</p>
                        </div>
                      </div>
                      {searchResult.is_friend ? (
                        <span className="text-green-400 text-sm flex items-center gap-1">
                          <Check className="w-4 h-4" /> Friend
                        </span>
                      ) : (
                        <Button
                          onClick={() => addFriend(searchResult.user_id)}
                          className="bg-pink-500 text-white hover:bg-pink-600 h-8 px-3"
                        >
                          <UserPlus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Friends List */}
                <div className="flex-1 overflow-y-auto">
                  <div className="px-3 py-2 bg-white/5">
                    <p className="text-xs text-[#94A3B8] font-medium flex items-center gap-1">
                      <Heart className="w-3 h-3 text-pink-400" /> My Friends ({friends.length})
                    </p>
                  </div>
                  {friends.length === 0 ? (
                    <div className="p-4 text-center text-[#94A3B8]">
                      <UserPlus className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No friends yet</p>
                      <p className="text-xs mt-1">Search by code to add friends</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {friends.map((friend) => (
                        <button
                          key={friend.user_id}
                          onClick={() => startConversation(friend.user_id, friend.name)}
                          className="w-full p-2 px-3 hover:bg-white/5 transition-colors text-left flex items-center gap-2"
                        >
                          <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-pink-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{friend.name}</p>
                            <p className="text-xs text-[#94A3B8]">{friend.worker_code || friend.role?.replace(/_/g, ' ')}</p>
                          </div>
                          <MessageSquare className="w-4 h-4 text-[#23F7DD]" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : showHelp ? (
              // Help - Contact Admins
              <div className="h-full flex flex-col">
                <div className="p-3 bg-yellow-500/10 border-b border-yellow-500/30">
                  <p className="text-sm text-yellow-400">
                    Contact an administrator for help
                  </p>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {admins.length === 0 ? (
                    <div className="p-4 text-center text-[#94A3B8]">
                      <HelpCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No admins available</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {admins.map((admin) => (
                        <button
                          key={admin.user_id}
                          onClick={() => startConversation(admin.user_id, admin.name)}
                          className="w-full p-3 hover:bg-white/5 transition-colors text-left flex items-center gap-3"
                        >
                          <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                            <HelpCircle className="w-5 h-5 text-yellow-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{admin.name}</p>
                            <p className="text-xs text-yellow-400">
                              {admin.role === 'platform_admin' ? 'Administrator' : 'Operations Manager'}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : showNewChat ? (
              // New Chat
              <div className="h-full flex flex-col">
                <div className="p-3 border-b border-white/10">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by name or code..."
                      className="bg-white/5 border-white/10 text-white pl-9 text-sm"
                    />
                  </div>
                </div>
                
                {/* Friends Section */}
                {friends.length > 0 && !searchTerm && (
                  <div className="border-b border-white/10">
                    <div className="px-3 py-2 bg-pink-500/10">
                      <p className="text-xs text-pink-400 font-medium flex items-center gap-1">
                        <Heart className="w-3 h-3" /> Friends
                      </p>
                    </div>
                    <div className="max-h-28 overflow-y-auto">
                      {friends.map((friend) => (
                        <button
                          key={friend.user_id}
                          onClick={() => startConversation(friend.user_id, friend.name)}
                          className="w-full p-2 px-3 hover:bg-white/5 transition-colors text-left flex items-center gap-2"
                        >
                          <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-pink-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{friend.name}</p>
                            <p className="text-xs text-[#94A3B8]">{friend.worker_code}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Project Chats */}
                {projectChats.length > 0 && !searchTerm && (
                  <div className="border-b border-white/10">
                    <div className="px-3 py-2 bg-white/5">
                      <p className="text-xs text-[#94A3B8] font-medium flex items-center gap-1">
                        <Users className="w-3 h-3" /> Project Chats
                      </p>
                    </div>
                    <div className="max-h-28 overflow-y-auto">
                      {projectChats.map((project) => (
                        <button
                          key={project.project_id}
                          onClick={() => joinProjectChat(project.project_id, project.title)}
                          className="w-full p-2 px-3 hover:bg-white/5 transition-colors text-left flex items-center gap-2"
                        >
                          <span className="text-lg">📋</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{project.title}</p>
                            <p className="text-xs text-[#94A3B8]">{project.members_count} members</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Team Members / Search Results */}
                <div className="flex-1 overflow-y-auto">
                  {filteredMembers.length === 0 && searchTerm ? (
                    <div className="p-4 text-center text-[#94A3B8]">
                      <p>No users found for &quot;{searchTerm}&quot;</p>
                      <button
                        onClick={() => {
                          setShowNewChat(false);
                          setShowAddFriend(true);
                          setCodeSearchTerm(searchTerm);
                        }}
                        className="mt-2 text-sm text-pink-400 hover:underline"
                      >
                        Search by exact code instead
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {filteredMembers.map((member) => (
                        <button
                          key={member.user_id}
                          onClick={() => startConversation(member.user_id, member.name)}
                          className="w-full p-2 px-3 hover:bg-white/5 transition-colors text-left flex items-center gap-2"
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            member.is_friend ? 'bg-pink-500/20' : 'bg-[#23F7DD]/20'
                          }`}>
                            <User className={`w-4 h-4 ${member.is_friend ? 'text-pink-400' : 'text-[#23F7DD]'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">
                              {member.name}
                              {member.is_friend && <Heart className="w-3 h-3 text-pink-400 inline ml-1" />}
                            </p>
                            <p className="text-xs text-[#94A3B8]">
                              {member.worker_code || member.role?.replace(/_/g, ' ')}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : !activeConversation ? (
              // Conversations List
              <div className="h-full overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="p-4 text-center text-[#94A3B8]">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No conversations yet</p>
                    <p className="text-xs mt-1">Click + to start a chat</p>
                    <div className="mt-3 flex flex-col gap-2">
                      <button
                        onClick={() => setShowAddFriend(true)}
                        className="text-sm text-pink-400 hover:underline flex items-center gap-1 mx-auto"
                      >
                        <UserPlus className="w-4 h-4" /> Add a friend by code
                      </button>
                      <button
                        onClick={() => setShowHelp(true)}
                        className="text-sm text-yellow-400 hover:underline flex items-center gap-1 mx-auto"
                      >
                        <HelpCircle className="w-4 h-4" /> Need help?
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {conversations.map((conv) => (
                      <button
                        key={conv.conversation_id}
                        onClick={() => setActiveConversation(conv)}
                        className="w-full p-3 hover:bg-white/5 transition-colors text-left flex items-center gap-3"
                      >
                        <div className="w-10 h-10 rounded-full bg-[#23F7DD]/20 flex items-center justify-center flex-shrink-0">
                          {conv.is_project_chat ? (
                            <span>📋</span>
                          ) : (
                            <User className="w-5 h-5 text-[#23F7DD]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {conv.participant_name}
                          </p>
                          <p className="text-xs text-[#94A3B8] truncate">
                            {conv.last_message || "No messages yet"}
                          </p>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="w-5 h-5 bg-[#23F7DD] text-[#050505] text-xs rounded-full flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Messages View
              <div className="h-full flex flex-col">
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-[#94A3B8] text-sm">
                      Start the conversation!
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] p-2.5 rounded-2xl ${
                            msg.sender_id === user?.user_id
                              ? "bg-[#23F7DD] text-[#050505] rounded-br-sm"
                              : "bg-white/10 text-white rounded-bl-sm"
                          }`}
                        >
                          {activeConversation.is_project_chat && msg.sender_id !== user?.user_id && (
                            <p className={`text-xs font-medium mb-1 ${
                              msg.sender_id === user?.user_id ? "text-[#050505]/70" : "text-[#23F7DD]"
                            }`}>
                              {msg.sender_name}
                            </p>
                          )}
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === user?.user_id ? "text-[#050505]/60" : "text-[#94A3B8]"
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-2 border-t border-white/10 flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Type a message..."
                    className="bg-white/5 border-white/10 text-white text-sm"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={loading || !newMessage.trim()}
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] px-3"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
